import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../../components/base/Dialog/Dialog';
import { additionalCarrierColumns, carrierColumns } from './carrierColumns';
import { additionalSzltColumns, szltColumns } from './szltColumns';
import { additionalVehicleColumns, vehicleColumns } from './vehiclesColumns';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import Pagination from '../../../components/base/Pagination/Pagination';
import { getRegisterData } from '../reducer';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import {
  calculatedViewMode,
  getPaginationOpts,
  responseLogWithNotification,
} from '../../../utils/utils';
import getDefaultDataRange from '../../../utils/getDefaultDataRange';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import TextInput from '../../../components/fields/TextInput/TextInput';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import { REGISTRIES_STATUS_LIST } from '../../../configs';
import ApplicationsDTO from '../../../dto/ApplicationsDTO';
import {
  loadCarrierListModal,
  loadSZLTListModal,
  loadVehiclesListModal,
} from '../actions';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import getDefaultSort from '../../../utils/getDefaultSort';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import TextSearchInput from '../../../components/fields/TextInput/TextSearchInput';

export default function RegistryPageModal(props) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const {
    carrierListModal = [],
    vehiclesListModal = [],
    szltListModal = [],
    szltListModalMeta = [],
    carrierListModalMeta = [],
    vehiclesListModalMeta = {},
    loadingCarrierListModal = false,
    loadingVehiclesListModal = false,
    loadingSzltListModal = false,
  } = useSelector(getRegisterData);
  const {
    type = 'carrier', onClose = () => {}, numbers = [], customFilter = {}, title = '', filterName = '',
  } = props;
  const navigate = useNavigate();

  const DTO = new ApplicationsDTO();
  const attributes = DTO.getAttributesByName();
  const defaultDate = getDefaultDataRange();
  const defaultFilterValues = {
    search: '', created_at: '', ...customFilter,
  };
  const {
    control, watch, setValue, getValues,
  } = useForm({
    defaultValues: defaultFilterValues,
  });

  const typeMap = {
    carrier: {
      meta: carrierListModalMeta,
      list: carrierListModal,
      columns: carrierColumns,
      additionalColumns: additionalCarrierColumns,
      action: loadCarrierListModal,
      loadingList: loadingCarrierListModal,
    },
    szlt: {
      meta: szltListModalMeta,
      list: szltListModal,
      columns: szltColumns,
      additionalColumns: additionalSzltColumns,
      action: loadSZLTListModal,
      loadingList: loadingSzltListModal,
    },
    default: {
      meta: vehiclesListModalMeta,
      list: vehiclesListModal,
      columns: vehicleColumns,
      additionalColumns: additionalVehicleColumns,
      action: loadVehiclesListModal,
      loadingList: loadingVehiclesListModal,
    },
  };

  const config = typeMap[type] || typeMap.default;
  const {
    meta, list, columns, additionalColumns, action, loadingList,
  } = config;

  const loadData = opts => {
    const filterData = getValues();
    const viewModeData = calculatedViewMode(filterData.view_mode);
    delete filterData.view_mode;
    const paginationOpts = getPaginationOpts(meta, opts);
    const requestData = {
      ...paginationOpts,
      filter: { ...filterData, ...viewModeData },
      sort,
    };
    const p1 = dispatch(action(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const [sort, setSort] = useState(getDefaultSort(columns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  useEffect(() => { loadData(); }, [sort]);

  useEffect(() => {
    dispatch(action({
      filter: {
        ...getValues(),
      },
    })).catch(responseLogWithNotification);
  }, []);

  useEffect(() => {
    const subscription = watch(values => {
      loadData({
        page: 1,
      });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Dialog
      className="registry-relation-history-dialog"
      onClose={onClose}
      open
    >
      <DialogTitle header={title || `${formatMessage({ id: `menu.region.registry.${type.replace('-', '.')}` })} (из связей)`} title={<Icons onClick={onClose} customIcon icon="close" className="close" />} />
      <DialogContent className="registry-relation-history-content">
        <TimerLoader loading={loadingList} />
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextSearchInput}
            dtoObject={{
              name: 'search',
              type: String,
              labelKey: 'search',
            }}
            className="no-border"
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.status}
            usePortal
            className="no-border"
            options={REGISTRIES_STATUS_LIST}
            placeholder="Статус"
            addPlaceholder
          />
          <FieldDTO
            control={control}
            component={RangeDatePicker}
            dtoObject={attributes.created_at}
            usePortal
            placeholder="Дата"
            changeFormat="YYYY-MM-DD"
            inputProps={{
              value: [getValues('created_at_from'), getValues('created_at_to')],
              onChange: e => {
                const v = e.target.value;
                setValue('created_at_from', v[0]);
                setValue('created_at_to', v[1]);
              },
            }}
            className="no-border"
          />
        </FilterRow>
        <div className="registry-relation-history-table  custom-scroll-theme sticky-table-wrapper">
          <CustomizedGrid
            data={list}
            changeSort={changeSort}
            sort={sort}
            onDoubleClickByRow={(item, _) => navigate(`/region-registry-${type}?registry_id=${item?.id}`)}
            columns={columns}
            name={`${type}-list-modal`}
            additionalColumns={additionalColumns}
          />
        </div>
      </DialogContent>
      <DialogFooter>
        <Pagination meta={meta} list={list} handleChangeMeta={loadData} />
      </DialogFooter>
    </Dialog>
  );
}
